/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';
import { helperFunctions } from '../../../../utils/helper';

interface TubeDetailsViewModalProps {
  showView: boolean;
  setShowView: (value: boolean) => void;
  tubeId?: string;
  selectedTubeData: any;
}

type TabKey =
  | "sterilityTest"
  | "incubation"
  | "labObservation"
  | "labApproval"
  | "autoclave";

const STATUS_BADGE_STYLE: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 12px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  backgroundColor: '#E6F6EC',
  color: '#1E8E3E',
};

const COUNT_BADGE_STYLE: React.CSSProperties = {
  marginLeft: '8px',
  padding: '0 8px',
  borderRadius: '10px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: '#EEF2F7',
  color: '#475569',
};

// ---------- small helpers ----------
const val = (v: any) => (v === null || v === undefined || String(v).trim() === "" ? "-" : String(v));

const dt = (v: any) => (v ? helperFunctions.formatDate(v, true) : "-");

const fullName = (first?: string, last?: string, fallback?: string) => {
  const name = `${first || ""} ${last || ""}`.trim();
  return name || val(fallback);
};

// "approved_by_name" -> "Approved By Name" (used only for autoclave where the shape is unknown)
const humanize = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const isDateKey = (key: string) => /(date|time|_at)$/i.test(key);

const TubeDetailsViewModal: React.FC<TubeDetailsViewModalProps> = ({
  showView,
  setShowView,
  selectedTubeData,
}) => {

  const { t } = useTranslation();

  const [tabValue, setTabValue] = useState<TabKey>("sterilityTest");

  // ---------- map selectedTubeData directly (no API call) ----------
  const summary = useMemo(() => {
    const d = selectedTubeData || {};
    return {
      serialNumber: val(d.serial_number),
      batchNumber: val(d.batch_number),
      lotCode: val(d.lot_code),
      productName: val(d.product_name),
      activityName: val(d.assigned_activity_name),
      operatorName: val(d.assigned_operator_name),
      nextProcess: val(d.next_process_keyword),
      manufacturingDate: dt(d.manufacturing_date),
      expiryDate: dt(d.expiry_date),
      status: val(d.status),
    };
  }, [selectedTubeData]);

  const sterilityTests = useMemo(
    () =>
      (selectedTubeData?.sterility_test_details || []).map((e: any) => ({
        identifier: val(e.sterility_test_identifier),
        testMethod: val(e.test_method_name),
        testType: val(e.test_type_name),
        startedBy: fullName(e.started_by_user_firstname, e.started_by_user_lastname, e.created_by_name),
        startDateTime: dt(e.start_date_time),
        completedBy: fullName(e.ended_user_firstname, e.ended_user_lastname, e.updated_by_name),
        completedDateTime: dt(e.completed_date_time),
        serialNumber: val(e.serial_number),
        batchNumber: val(e.batch_number),
      })),
    [selectedTubeData]
  );

  const incubations = useMemo(
    () =>
      (selectedTubeData?.incubation_details || []).map((e: any) => ({
        incubatorName: val(e.incubator_name),
        incubationArea: val(e.incubation_area_name),
        incubationDays: val(e.incubation_days),
        startDateTime: dt(e.start_date_time),
        expectedEndTime: dt(e.expected_end_time),
        endDateTime: dt(e.end_date_time),
        minTemperature: val(e.incubator_min_temperature),
        maxTemperature: val(e.incubator_max_temperature),
        bufferTime: dt(e.buffer_time_for_stop_process),
        overdueTime: dt(e.overdue_time),
        status: val(e.status).replace(/_/g, " "),
        createdBy: val(e.created_by_name),
        updatedBy: val(e.updated_by_name),
      })),
    [selectedTubeData]
  );

  const labObservations = useMemo(
    () =>
      (selectedTubeData?.lab_observation_details || []).map((e: any) => ({
        scheduleId: val(e.observation_schedule_id),
        observationDay: val(e.observation_day),
        scheduledDateTime: dt(e.scheduled_date_time),
        observationDateTime: dt(e.observation_date_time),
        visualClarity: val(e.visual_clarity_assessment),
        results: val(e.observation_results),
        remarks: val(e.remarks),
        observedBy: val(e.observed_by_name),
        incubatorName: val(e.incubator_name),
      })),
    [selectedTubeData]
  );

  const labApprovals = useMemo(
    () =>
      (selectedTubeData?.lab_approval_details || []).map((e: any) => ({
        identifier: val(e.sterility_test_identifier),
        testMethod: val(e.test_method_name),
        approvalStatus: val(e.approval_status),
        approvedBy: val(e.approved_by_name),
        approvedDate: dt(e.approved_date),
        startDateTime: dt(e.start_date_time),
        completedAt: dt(e.completed_at),
        createdBy: val(e.created_by_name),
        updatedBy: val(e.updated_by_name),
      })),
    [selectedTubeData]
  );

  // autoclave_details has no sample payload yet, so render whatever keys the API returns
  const autoclaves = useMemo(
    () =>
      (selectedTubeData?.autoclave_details || []).map((e: any) =>
        Object.entries(e || {})
          .filter(([key]) => !/(^id$|_id$)/i.test(key))
          .map(([key, value]) => ({
            label: humanize(key),
            value: isDateKey(key) ? dt(value) : val(value),
          }))
      ),
    [selectedTubeData]
  );

  const sidebarItems: { key: TabKey; label: string; count: number }[] = [
    { key: "sterilityTest", label: t("tubeDetailsView.sterilityTestDetails", { defaultValue: "Sterility Test Details" }), count: sterilityTests.length },
    { key: "incubation", label: t("tubeDetailsView.incubationDetails", { defaultValue: "Incubation Details" }), count: incubations.length },
    { key: "labObservation", label: t("tubeDetailsView.labObservationDetails", { defaultValue: "Lab Observation Details" }), count: labObservations.length },
    { key: "labApproval", label: t("tubeDetailsView.labApprovalDetails", { defaultValue: "Lab Approval Details" }), count: labApprovals.length },
    { key: "autoclave", label: t("tubeDetailsView.autoclaveDetails", { defaultValue: "Autoclave Details" }), count: autoclaves.length },
  ];

  // close tube details modal
  const handleClose = () => {
    setShowView(false);
    setTabValue("sterilityTest");
  };

  // add css to selected sidebar item
  const getItemClass = (value: TabKey) => {
    return `custom-list-group-item ${tabValue === value ? 'selected' : ''}`;
  };

  // ---------- reusable renderers ----------
  const Field = ({ label, value, badge = false }: { label: string; value: string; badge?: boolean }) => (
    <div className="col-md-4">
      <p className="plate-detail-key">{label}</p>
      <p className="plate-detail-value" style={badge ? STATUS_BADGE_STYLE : undefined}>{value}</p>
    </div>
  );

  const NoData = () => (
    <p className="">{t("tubeDetailsView.noData", { defaultValue: "No data available" })}</p>
  );

  // title + "n of total" label when a section has multiple records
  const EntryHeader = ({ index, total }: { index: number; total: number }) =>
    total > 1 ? (
      <p className="plate-detail-key" style={{ fontWeight: 600, marginTop: index === 0 ? 0 : 16 }}>
        #{index + 1} / {total}
      </p>
    ) : null;

  return (
    <Modal className="view-modal" show={showView} size="lg">
      <Modal.Header closeButton onClick={handleClose}>
        <Modal.Title className="view-header">{t("tubeDetailsView.view")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="view-body">
        <div className="view-body-content">
          <p className="plate-details">{t("tubeDetailsView.tubeDetails")}</p>
          <div className="view-body-details">
            <div className="row">
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.serialNumber", { defaultValue: "Serial Number" })}</p>
                <p className="plate-detail-value">{summary.serialNumber}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.batchNumber", { defaultValue: "Batch Number" })}</p>
                <p className="plate-detail-value">{summary.batchNumber}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.lotCode", { defaultValue: "Lot Code" })}</p>
                <p className="plate-detail-value">{summary.lotCode}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.productName", { defaultValue: "Product" })}</p>
                <p className="plate-detail-value">{summary.productName}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.activity", { defaultValue: "Activity" })}</p>
                <p className="plate-detail-value">{summary.activityName}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.operator", { defaultValue: "Operator" })}</p>
                <p className="plate-detail-value">{summary.operatorName}</p>
              </div>
            </div>
            <div className="row">
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.manufacturingDate", { defaultValue: "Manufacturing Date" })}</p>
                <p className="plate-detail-value">{summary.manufacturingDate}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.expiryDate", { defaultValue: "Expiry Date" })}</p>
                <p className="plate-detail-value">{summary.expiryDate}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.nextProcess", { defaultValue: "Next Process" })}</p>
                <p className="plate-detail-value" style={{ textTransform: 'capitalize' }}>{summary.nextProcess}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.currentStatus")}</p>
                <p className="plate-detail-value" style={STATUS_BADGE_STYLE}>{summary.status}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="view-lab-details">
          <div className="view-lab-details-content">
            <div className="row">
              <ul className="custom-list-group col-md-3">
                {sidebarItems.map((item) => (
                  <li key={item.key} className={getItemClass(item.key)} onClick={() => setTabValue(item.key)}>
                    {item.label}
                    {/* <span style={COUNT_BADGE_STYLE}>{item.count}</span> */}
                  </li>
                ))}
              </ul>

              <div className="col-md-9 tube-detail-sections">

                {/* Sterility Test Details */}
                {tabValue === "sterilityTest" && (
                  <div className="detail-section">
                    <p className="lab-detail-key">{sidebarItems[0].label}</p>
                    {sterilityTests.length === 0 ? <NoData /> : sterilityTests.map((e: any, i: number) => (
                      <div key={i}>
                        <EntryHeader index={i} total={sterilityTests.length} />
                        <div className="detail-col-1 row">
                          <Field label={t("tubeDetailsView.sterilityTestId", { defaultValue: "Sterility Test ID" })} value={e.identifier} />
                          <Field label={t("tubeDetailsView.testMethod", { defaultValue: "Test Method" })} value={e.testMethod} />
                          <Field label={t("tubeDetailsView.testType", { defaultValue: "Test Type" })} value={e.testType} />
                          <Field label={t("tubeDetailsView.startedBy", { defaultValue: "Started By" })} value={e.startedBy} />
                          <Field label={t("tubeDetailsView.startDateTime", { defaultValue: "Start Date & Time" })} value={e.startDateTime} />
                          <Field label={t("tubeDetailsView.completedBy", { defaultValue: "Completed By" })} value={e.completedBy} />
                          <Field label={t("tubeDetailsView.completedDateTime", { defaultValue: "Completed Date & Time" })} value={e.completedDateTime} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Incubation Details */}
                {tabValue === "incubation" && (
                  <div className="detail-section">
                    <p className="lab-detail-key">{sidebarItems[1].label}</p>
                    {incubations.length === 0 ? <NoData /> : incubations.map((e: any, i: number) => (
                      <div key={i}>
                        <EntryHeader index={i} total={incubations.length} />
                        <div className="detail-col-1 row">
                          <Field label={t("tubeDetailsView.incubatorName", { defaultValue: "Incubator" })} value={e.incubatorName} />
                          <Field label={t("tubeDetailsView.incubationArea", { defaultValue: "Incubation Area" })} value={e.incubationArea} />
                          <Field label={t("tubeDetailsView.incubationDays", { defaultValue: "Incubation Days" })} value={e.incubationDays} />
                          <Field label={t("tubeDetailsView.startDateTime", { defaultValue: "Start Date & Time" })} value={e.startDateTime} />
                          <Field label={t("tubeDetailsView.expectedEndTime", { defaultValue: "Expected End Time" })} value={e.expectedEndTime} />
                          <Field label={t("tubeDetailsView.endDateTime", { defaultValue: "End Date & Time" })} value={e.endDateTime} />
                          <Field label={t("tubeDetailsView.minTemperature", { defaultValue: "Min Temperature (°C)" })} value={e.minTemperature} />
                          <Field label={t("tubeDetailsView.maxTemperature", { defaultValue: "Max Temperature (°C)" })} value={e.maxTemperature} />
                          <Field label={t("tubeDetailsView.currentStatus")} value={e.status} badge />
                          <Field label={t("tubeDetailsView.bufferTime", { defaultValue: "Buffer Time For Stop" })} value={e.bufferTime} />
                          <Field label={t("tubeDetailsView.overdueTime", { defaultValue: "Overdue Time" })} value={e.overdueTime} />
                          <Field label={t("tubeDetailsView.createdBy", { defaultValue: "Created By" })} value={e.createdBy} />
                          <Field label={t("tubeDetailsView.updatedBy", { defaultValue: "Updated By" })} value={e.updatedBy} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Lab Observation Details (many rows -> table) */}
                {tabValue === "labObservation" && (
                  <div className="detail-section">
                    <p className="lab-detail-key">{sidebarItems[2].label}</p>
                    {labObservations.length === 0 ? <NoData /> : (
                      <div style={{ overflowX: 'auto' }}>
                        <table className="table">
                          <thead>
                            <tr style={{ fontSize: "14px" }}>
                              <th scope="col">{t("tubeDetailsView.observationDay", { defaultValue: "Day" })}</th>
                              <th scope="col">{t("tubeDetailsView.scheduledDateTime", { defaultValue: "Scheduled On" })}</th>
                              <th scope="col">{t("tubeDetailsView.observationDateTime", { defaultValue: "Observed On" })}</th>
                              <th scope="col">{t("tubeDetailsView.visualClarity", { defaultValue: "Visual Clarity" })}</th>
                              <th scope="col">{t("tubeDetailsView.observationResults", { defaultValue: "Result" })}</th>
                              <th scope="col">{t("tubeDetailsView.observedBy", { defaultValue: "Observed By" })}</th>
                              <th scope="col">{t("tubeDetailsView.remarks")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {labObservations.map((e: any, i: number) => (
                              <tr key={e.scheduleId + "-" + i} style={{ fontSize: "11px" }}>
                                <td>{e.observationDay}</td>
                                <td>{e.scheduledDateTime}</td>
                                <td>{e.observationDateTime}</td>
                                <td>{e.visualClarity}</td>
                                <td>{e.results}</td>
                                <td>{e.observedBy}</td>
                                <td>{e.remarks}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Lab Approval Details */}
                {tabValue === "labApproval" && (
                  <div className="detail-section">
                    <p className="lab-detail-key">{sidebarItems[3].label}</p>
                    {labApprovals.length === 0 ? <NoData /> : labApprovals.map((e: any, i: number) => (
                      <div key={i}>
                        <EntryHeader index={i} total={labApprovals.length} />
                        <div className="detail-col-1 row">
                          <Field label={t("tubeDetailsView.sterilityTestId", { defaultValue: "Sterility Test ID" })} value={e.identifier} />
                          <Field label={t("tubeDetailsView.testMethod", { defaultValue: "Test Method" })} value={e.testMethod} />
                          <Field label={t("tubeDetailsView.approvalStatus", { defaultValue: "Approval Status" })} value={e.approvalStatus} badge />
                          <Field label={t("tubeDetailsView.approvedBy", { defaultValue: "Approved By" })} value={e.approvedBy} />
                          <Field label={t("tubeDetailsView.approvedDate", { defaultValue: "Approved Date" })} value={e.approvedDate} />
                          <Field label={t("tubeDetailsView.startDateTime", { defaultValue: "Start Date & Time" })} value={e.startDateTime} />
                          <Field label={t("tubeDetailsView.completedAt", { defaultValue: "Completed At" })} value={e.completedAt} />
                          <Field label={t("tubeDetailsView.createdBy", { defaultValue: "Created By" })} value={e.createdBy} />
                          <Field label={t("tubeDetailsView.updatedBy", { defaultValue: "Updated By" })} value={e.updatedBy} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Autoclave Details */}
                {tabValue === "autoclave" && (
                  <div className="detail-section">
                    <p className="lab-detail-key">{sidebarItems[4].label}</p>
                    {autoclaves.length === 0 ? <NoData /> : autoclaves.map((fields: any[], i: number) => (
                      <div key={i}>
                        <EntryHeader index={i} total={autoclaves.length} />
                        <div className="detail-col-1 row">
                          {fields.map((f) => (
                            <Field key={f.label} label={f.label} value={f.value} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="custom-modal-footer">
        <Button className="footerCancelBtn" variant="tertiory" onClick={handleClose}>
          {t("buttons.cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default TubeDetailsViewModal;